/**
 * Listas dos seletores de cargo, gestor e centro de custo das telas de Pessoas: o valor atual sempre
 * visível (mesmo inativo, com o motivo) e, nos centros, os do departamento primeiro. Tudo vem de
 * /people/options, que respeita o acesso de quem está na tela: aqui nada amplia o que a pessoa vê.
 */
import { costCenterDepartmentOf, costCenterLabel, eligibleCostCenters } from '@loot/shared';
import type { SearchOption } from '../../components/search-select.ts';
import type { PeopleOptions } from './common.tsx';

/** Cargo como "código · nome de mercado (nível)". */
export function jobLabel(j: {
  code: string;
  name: string;
  marketName?: string | null;
  levelCode?: string | null;
}): string {
  return `${j.code} · ${j.marketName ?? j.name}${j.levelCode ? ` (${j.levelCode})` : ''}`;
}

/** Item extra para um valor gravado que não está na lista (inativo ou fora do acesso). */
function savedOption(id: string, label: string | null, group: string, inactive: string) {
  return {
    id,
    label: label ?? id,
    group,
    tag: { label: inactive, tone: 'off' as const },
  } satisfies SearchOption;
}

/**
 * Cargos compatíveis com o departamento (ADR 0096): pela família, pela subfamília principal ou de
 * atuação, ou por exceção. Sem departamento ou sem ligação cadastrada, null: não há filtro.
 */
export function compatibleJobs(
  options: Pick<PeopleOptions, 'jobs' | 'jobSubfamilies' | 'departmentJobScopes'>,
  departmentId: string | null,
): Set<string> | null {
  const links = options.departmentJobScopes.filter((l) => l.departmentId === departmentId);
  if (!departmentId || links.length === 0) return null;
  const families = new Set(links.flatMap((l) => l.familyId ?? []));
  const subfamilies = new Set(links.flatMap((l) => l.subfamilyId ?? []));
  return new Set([
    ...links.flatMap((l) => l.jobId ?? []),
    ...options.jobs.filter((j) => j.familyId && families.has(j.familyId)).map((j) => j.id),
    ...options.jobSubfamilies.filter((s) => subfamilies.has(s.subfamilyId)).map((s) => s.jobId),
  ]);
}

/**
 * Cargos. O atual vem primeiro, depois os compatíveis com o departamento e os demais, marcados:
 * a ligação só ordena, nunca esconde nem bloqueia. Cargo inativo que não é o atual só aparece para
 * quem o recebe da API (o Master), marcado e sem poder ser novo destino.
 */
export function jobChoices(
  options: Pick<PeopleOptions, 'jobs' | 'jobSubfamilies'>,
  current: { id: string | null; label: string | null },
  text: {
    current: string;
    others: string;
    inactive: string;
    compatible: string;
    otherJobs: string;
    outside: string;
  },
  compatible: ReadonlySet<string> | null = null,
): SearchOption[] {
  const inactive = { label: text.inactive, tone: 'off' as const };
  const toOption = (j: PeopleOptions['jobs'][number]): SearchOption => ({
    id: j.id,
    label: jobLabel(j),
    detail: [j.familyName, j.subfamilyName].filter(Boolean).join(' · ') || undefined,
    searchText: [
      j.name,
      j.marketName,
      ...options.jobSubfamilies.filter((s) => s.jobId === j.id).map((s) => s.name),
    ]
      .filter(Boolean)
      .join(' '),
    ...(j.status === 'inactive' ? { tag: inactive } : {}),
  });
  const rest = options.jobs
    .filter((j) => j.id !== current.id)
    .sort((a, b) => Number(a.status === 'inactive') - Number(b.status === 'inactive'))
    .map((j) => ({ ...toOption(j), disabled: j.status === 'inactive' }));
  const list = compatible
    ? [
        ...rest.filter((o) => compatible.has(o.id)).map((o) => ({ ...o, group: text.compatible })),
        ...rest
          .filter((o) => !compatible.has(o.id))
          .map((o) => ({
            ...o,
            group: text.otherJobs,
            tag: o.tag ?? { label: text.outside, tone: 'accent' as const },
          })),
      ]
    : rest;
  if (!current.id) return list;
  const saved = options.jobs.find((j) => j.id === current.id);
  return [
    saved
      ? { ...toOption(saved), group: text.current }
      : savedOption(current.id, current.label, text.current, text.inactive),
    ...(compatible ? list : list.map((o) => ({ ...o, group: text.others }))),
  ];
}

/** Gestores (a própria pessoa nunca); o atual aparece mesmo fora da lista. */
export function managerChoices(
  options: Pick<PeopleOptions, 'managers'>,
  selfId: string,
  current: { id: string | null; label: string | null },
  text: { current: string; others: string; inactive: string },
): SearchOption[] {
  const list: SearchOption[] = options.managers
    .filter((m) => m.id !== selfId)
    .map((m) => ({
      id: m.id,
      label: m.name,
      detail: [m.jobName, m.legalEntityCode].filter(Boolean).join(' · ') || undefined,
    }));
  if (!current.id || list.some((o) => o.id === current.id)) return list;
  return [
    savedOption(current.id, current.label, text.current, text.inactive),
    ...list.map((o) => ({ ...o, group: text.others })),
  ];
}

/**
 * Centros de custo da empresa, válidos na data: o oficial do departamento, depois os do
 * departamento (o do próprio centro ou o herdado do superior) e por fim os demais, marcados como
 * fora do departamento. Sem departamento escolhido, todos juntos. Nada é bloqueado aqui.
 */
export function costCenterChoices(
  options: Pick<PeopleOptions, 'costCenters' | 'departments'>,
  filter: {
    legalEntityId: string | null;
    departmentId: string | null;
    onDate: string;
    current: { id: string | null; label: string | null };
  },
  text: {
    official: string;
    department: string;
    others: string;
    outside: string;
    current: string;
    inactive: string;
    level: (level: string) => string;
  },
): SearchOption[] {
  const { legalEntityId, departmentId, onDate, current } = filter;
  const eligible = legalEntityId
    ? eligibleCostCenters(options.costCenters, { legalEntityId, onDate })
    : [];
  const byId = new Map(options.costCenters.map((c) => [c.id, c]));
  const official = options.departments.find((d) => d.id === departmentId)?.costCenterId ?? null;
  const toOption = (c: (typeof eligible)[number], extra: Partial<SearchOption>): SearchOption => ({
    id: c.id,
    label: costCenterLabel(c.code, c.name),
    ...(c.structureLevel ? { detail: text.level(c.structureLevel) } : {}),
    ...extra,
  });
  const inDepartment = (id: string) => costCenterDepartmentOf(id, byId) === departmentId;
  const list = !departmentId
    ? eligible.map((c) => toOption(c, {}))
    : [
        ...eligible
          .filter((c) => c.id === official)
          .map((c) => toOption(c, { group: text.official })),
        ...eligible
          .filter((c) => c.id !== official && inDepartment(c.id))
          .map((c) => toOption(c, { group: text.department })),
        ...eligible
          .filter((c) => c.id !== official && !inDepartment(c.id))
          .map((c) =>
            toOption(c, {
              group: text.others,
              tag: { label: text.outside, tone: 'accent' },
            }),
          ),
      ];
  if (!current.id || list.some((o) => o.id === current.id)) return list;
  return [savedOption(current.id, current.label, text.current, text.inactive), ...list];
}

/** Centros do rateio: os da empresa do vínculo e, à parte, os das outras empresas que a pessoa vê. */
export function allocationCenterChoices(
  options: Pick<PeopleOptions, 'costCenters' | 'legalEntities'>,
  legalEntityId: string | null,
  text: { own: string; others: string; otherCompany: string },
): SearchOption[] {
  const entity = (id: string) => options.legalEntities.find((e) => e.id === id)?.code ?? '?';
  return [
    ...options.costCenters
      .filter((c) => c.legalEntityId === legalEntityId)
      .map((c) => ({ id: c.id, label: costCenterLabel(c.code, c.name), group: text.own })),
    ...options.costCenters
      .filter((c) => c.legalEntityId !== legalEntityId)
      .map((c) => ({
        id: c.id,
        label: `${entity(c.legalEntityId)} · ${costCenterLabel(c.code, c.name)}`,
        group: text.others,
        tag: { label: text.otherCompany, tone: 'info' as const },
      })),
  ];
}
